import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  MessageSquare,
  Send,
  CheckCircle2,
  AlertTriangle,
  DollarSign,
  Activity,
  Key,
  TestTube,
  RefreshCw } from
'lucide-react';
import { toast } from 'sonner';

export const IntegrationsCenter = () => {const { t } = useTranslation("ezparcel");
  const { telegram, whatsapp } = communicationChannels;
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);

  const handleTestTelegram = () => {
    setTestingTelegram(true);
    toast.loading('Sending test message to Telegram...');

    setTimeout(() => {
      setTestingTelegram(false);
      toast.dismiss();
      toast.success('Test message sent successfully!', {
        description: 'Check your Telegram channel @ezparcel_ops'
      });
    }, 2000);
  };

  const handleTestWhatsApp = () => {
    setTestingWhatsApp(true);
    toast.loading('Sending test message via WhatsApp...');

    setTimeout(() => {
      setTestingWhatsApp(false);
      toast.dismiss();
      toast.success('Test message sent successfully!', {
        description: 'Message delivered to test number'
      });
    }, 2000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("communicationsIntegrations", "Communications & Integrations")}</h1>
        <p className="text-muted-foreground mt-2">{t("manageTelegramInternal", "Manage Telegram (internal) and WhatsApp (external) communication channels")}

        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-6 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-muted-foreground">{t("todaysMessages", "Today's Messages")}</p>
                <p className="text-2xl font-bold">
                  {costReport.today.telegram.messages + costReport.today.whatsapp.messages}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm text-muted-foreground">{t("whatsappCostToday", "WhatsApp Cost Today")}</p>
                <p className="text-2xl font-bold">${(costReport.today?.whatsapp?.cost ?? 0).toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Activity className="h-8 w-8 text-indigo-600" />
              <div>
                <p className="text-sm text-muted-foreground">{t("monthlyBudget", "Monthly Budget")}</p>
                <p className="text-lg font-bold">
                  ${costReport.thisMonth.whatsapp.cost}{t("key_7324", "/$")}{costReport.thisMonth.whatsapp.budget}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm text-muted-foreground">{t("activeChannels", "Active Channels")}</p>
                <p className="text-2xl font-bold">2</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Telegram Integration */}
      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle>{t("telegramIntegration", "Telegram Integration")}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">{t("internalSystemCommunications", "Internal system communications (FREE)")}</p>
              </div>
            </div>
            <Badge className="status-badge status-completed">
              <CheckCircle2 className="h-3 w-3 mr-1" />{t("connected", "Connected")}

            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">{t("botName", "Bot Name")}</Label>
                <div className="mt-2 p-3 bg-muted rounded-lg">
                  <p className="font-mono text-sm">{telegram.botName}</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">{t("botToken", "Bot Token")}</Label>
                <div className="mt-2 flex gap-2">
                  <Input
                    type="password"
                    value={telegram.botToken}
                    readOnly
                    className="font-mono" />
                  
                  <Button variant="outline" size="icon">
                    <Key className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">{t("operationsChannel", "Operations Channel")}</Label>
                <div className="mt-2 p-3 bg-muted rounded-lg">
                  <p className="font-mono text-sm">{telegram.channelId}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 border border-border rounded-lg">
                <p className="text-sm font-medium mb-3">{t("statisticsLast24", "Statistics (Last 24 hours)")}</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">{t("messagesSent", "Messages Sent")}</span>
                    <span className="font-medium">{telegram.messagesLastDay.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">{t("uptime", "Uptime")}</span>
                    <span className="font-medium text-green-600">{telegram.uptime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">{t("lastPing", "Last Ping")}</span>
                    <span className="font-medium text-xs">
                      {new Date(telegram.lastPing).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 border border-border rounded-lg">
                <p className="text-sm font-medium mb-2">{t("activeFeatures", "Active Features")}</p>
                <div className="flex flex-wrap gap-2">
                  {telegram.features.map((feature, idx) =>
                  <Badge key={idx} variant="outline" className="text-xs">
                      {feature}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={handleTestTelegram}
              disabled={testingTelegram}>
              
              <TestTube className="h-4 w-4 mr-2" />
              {testingTelegram ? 'Sending...' : 'Send Test Message'}
            </Button>
            <Button variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />{t("refreshConnection", "Refresh Connection")}

            </Button>
            <Button variant="outline">{t("viewLogs", "View Logs")}

            </Button>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp Integration */}
      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                <Send className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <CardTitle>{t("whatsappBusinessIntegration", "WhatsApp Business Integration")}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">{t("externalCustomerCommunications", "External customer communications (COST-MANAGED)")}</p>
              </div>
            </div>
            <Badge className="status-badge status-completed">
              <CheckCircle2 className="h-3 w-3 mr-1" />{t("connected", "Connected")}

            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">{t("provider", "Provider")}</Label>
                <div className="mt-2 p-3 bg-muted rounded-lg">
                  <p className="font-medium">{whatsapp.provider}</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">{t("accountSid", "Account SID")}</Label>
                <div className="mt-2 flex gap-2">
                  <Input
                    type="password"
                    value={whatsapp.accountSid}
                    readOnly
                    className="font-mono" />
                  
                  <Button variant="outline" size="icon">
                    <Key className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">{t("whatsappNumber", "WhatsApp Number")}</Label>
                <div className="mt-2 p-3 bg-muted rounded-lg">
                  <p className="font-mono">{whatsapp.phoneNumber}</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">{t("templateStatus", "Template Status")}</Label>
                <div className="mt-2 flex gap-3">
                  <div className="flex-1 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{whatsapp.approvedTemplates}</p>
                    <p className="text-xs text-green-700">{t("approved", "Approved")}</p>
                  </div>
                  <div className="flex-1 p-3 bg-orange-50 border border-orange-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-orange-600">{whatsapp.pendingApproval}</p>
                    <p className="text-xs text-orange-700">{t("pending", "Pending")}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium">{t("costManagement", "Cost Management")}</p>
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">{t("accountBalance", "Account Balance")}</span>
                      <span className="font-bold text-green-600">${(whatsapp.balance ?? 0).toFixed(2)}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">{t("monthlyBudget", "Monthly Budget")}</span>
                      <span className="font-medium">${whatsapp.monthlyBudget}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t("spentThisMonth", "Spent This Month")}</span>
                      <span className="font-medium">${whatsapp.monthlySpent.toFixed(2)}</span>
                    </div>
                    <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-600"
                        style={{ width: `${whatsapp.monthlySpent / whatsapp.monthlyBudget * 100}%` }} />
                      
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {(whatsapp.monthlySpent / whatsapp.monthlyBudget * 100).toFixed(1)}{t("used", "% used")}
                    </p>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t("costToday", "Cost Today")}</span>
                      <span className="font-medium">${whatsapp.costLastDay.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t("messagesToday", "Messages Today")}</span>
                      <span className="font-medium">{whatsapp.messagesLastDay}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 border border-border rounded-lg">
                <p className="text-sm font-medium mb-2">{t("activeFeatures", "Active Features")}</p>
                <div className="flex flex-wrap gap-2">
                  {whatsapp.features.map((feature, idx) =>
                  <Badge key={idx} variant="outline" className="text-xs">
                      {feature}
                    </Badge>
                  )}
                </div>
              </div>

              {whatsapp.balance < 50 &&
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-orange-900">{t("lowBalanceAlert", "Low Balance Alert")}</p>
                    <p className="text-xs text-orange-700 mt-1">{t("accountBalanceIs", "Account balance is below $50. Consider topping up to avoid service interruption.")}

                  </p>
                  </div>
                </div>
              }
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={handleTestWhatsApp}
              disabled={testingWhatsApp}>
              
              <TestTube className="h-4 w-4 mr-2" />
              {testingWhatsApp ? 'Sending...' : 'Send Test Message'}
            </Button>
            <Button variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />{t("refreshStatus", "Refresh Status")}

            </Button>
            <Button variant="outline">
              <DollarSign className="h-4 w-4 mr-2" />{t("topUpBalance", "Top Up Balance")}

            </Button>
            <Button variant="outline">{t("viewCostReport", "View Cost Report")}

            </Button>
          </div>
        </CardContent>
      </Card>
    </div>);

};